/**
 * Profile – About Me tab
 * HTML template for the About Me tab content
 */

import {
  MIN_AGE,
  MAX_AGE,
  MIN_HEIGHT_CM,
  MAX_HEIGHT_CM,
  MAX_HEADLINE_LENGTH,
  MAX_BIO_LENGTH,
} from '../../../config';
import { wantsKidsLabel } from '../../../utils';
import {
  ETHNICITY_OPTIONS,
  POLITICAL_VIEW_OPTIONS,
  BODY_TYPE_OPTIONS,
  FREQUENCY_OPTIONS,
  EXERCISE_OPTIONS,
  HAS_KIDS_OPTIONS,
  WANTS_MORE_KIDS_OPTIONS,
  PET_TYPE_OPTIONS,
  EDUCATION_LEVEL_OPTIONS,
  EMPLOYMENT_STATUS_OPTIONS,
  INCOME_RANGE_OPTIONS,
  FLEXIBILITY_OPTIONS_UI,
  SEXUAL_ROLE_OPTIONS,
  SEXUAL_DYNAMICS_OPTIONS,
  RELATIONSHIP_INTENT_OPTIONS,
  RELATIONSHIP_STRUCTURE_OPTIONS,
  SEXUAL_ORIENTATION_OPTIONS,
  RELIGION_OPTIONS,
  PRONOUNS_OPTIONS,
  LANGUAGES_OPTIONS,
  ZODIAC_OPTIONS,
  CAREER_FIELD_OPTIONS,
  MAJOR_OPTIONS,
  isPnta,
} from '../../../options';
import type { IntentEntry } from '../../../options';
import {
  renderPntaCheckbox,
  renderCheckboxGroup,
  renderRadioGroup,
  renderRadioGroupWithOther,
  renderMultiSelectWithOther,
  renderMultiSelectWithCustomField,
  renderVisiCheckbox,
} from '../form-renderers';
import { renderChildrenList } from '../children-manager';

function isPrivateName(name: string | null | undefined): boolean {
  return /^Private-\d{5}$/.test(name || '');
}

export interface AboutTabData {
  userId: string;
  profile: Record<string, any>;
  identity: Record<string, any>;
  body: Record<string, any>;
  lifestyle: Record<string, any>;
  pets: Record<string, any>;
  kids: Record<string, any>;
  background: Record<string, any>;
  about: Record<string, any>;
  relationship: Record<string, any>;
  profilePrefs: Record<string, any>;
  sexual: Record<string, any>;
  disclosures: Record<string, any>;
  genders: Array<{ id: number; name: string }>;
  relationshipStatuses: Array<{ id: number; name: string }>;
  units: string;
  visiJson: Record<string, any>;
  isActive: boolean;
}

const GENDER_OPTIONS = [
  { value: 'men', label: 'Man' },
  { value: 'women', label: 'Woman' },
  { value: 'nonbinary', label: 'Non-binary' },
];

export function renderProfileIntentCards(entries: IntentEntry[]): string {
  if (!entries || entries.length === 0) {
    return '<div class="empty-intent-entries">You haven\'t added anything yet. Add one or more to describe how you want to connect.</div>';
  }
  return entries.map((entry, i) => `
    <div class="intent-entry-card" data-entry-index="${i}">
      <button type="button" class="btn-remove-intent-entry" data-entry-index="${i}" title="Remove">
        <i class="fas fa-times"></i>
      </button>
      <div class="intent-entry-fields">
        <div class="intent-entry-field">
          <span class="group-label">Their Gender</span>
          <div class="checkbox-group">
            ${GENDER_OPTIONS.map(opt => `
              <label class="checkbox-item">
                <input
                  type="checkbox"
                  name="profile_intent_genders_${i}"
                  value="${opt.value}"
                  ${entry.genders?.includes(opt.value as any) ? 'checked' : ''}
                  data-entry-index="${i}"
                  data-entry-field="genders"
                />
                ${opt.label}
              </label>
            `).join('')}
          </div>
        </div>
        <div class="intent-entry-field">
          <span class="group-label">Connection Goals</span>
          <div class="checkbox-group">
            ${RELATIONSHIP_INTENT_OPTIONS.filter(o => o.value && o.value !== 'pnta').map(opt => `
              <label class="checkbox-item">
                <input
                  type="checkbox"
                  name="profile_intent_intents_${i}"
                  value="${opt.value}"
                  ${entry.intents?.includes(opt.value as any) ? 'checked' : ''}
                  data-entry-index="${i}"
                  data-entry-field="intents"
                />
                ${opt.label}
              </label>
            `).join('')}
          </div>
        </div>
        <div class="intent-entry-field">
          <div class="preference-row">
            <span class="group-label">Age Range</span>
            <span class="filter-value intent-entry-age-display" data-entry-index="${i}">${(entry.age_min ?? 18) === 18 && (entry.age_max ?? 99) === 99 ? 'Any' : `${entry.age_min ?? 18}-${entry.age_max ?? 99}`}</span>
          </div>
          <div class="flex gap-2">
            <input type="range" class="input intent-entry-age-min" data-entry-index="${i}" min="18" max="99" value="${entry.age_min ?? 18}" />
            <input type="range" class="input intent-entry-age-max" data-entry-index="${i}" min="18" max="99" value="${entry.age_max ?? 99}" />
          </div>
        </div>
      </div>
    </div>
  `).join('');
}

export function renderAboutTab(d: AboutTabData): string {
  return `
      <div class="view-scroll seg-panel${d.isActive ? ' active' : ''}" id="tab-about">
        <div id="profile-form" class="profile-edit-form" data-lpignore="true" data-1p-ignore autocomplete="off">

          <!-- MY PHOTOS STRIP -->
          <div class="form-section">
            <div class="form-section-header" data-section="photos-strip">
              <h3><i class="fas fa-camera"></i> My Photos</h3>
              <span class="section-progress" data-section-progress="photos-strip"></span>
              <i class="fas fa-chevron-down section-toggle"></i>
            </div>
            <div class="form-section-content" id="section-photos-strip">
              <div id="about-photos-strip" class="about-photos-strip">
                <!-- Populated by photo-manager after photos load -->
              </div>
            </div>
          </div>

          <!-- BASICS SECTION -->
          <div class="form-section">
            <div class="form-section-header" data-section="basics">
              <h3><i class="fas fa-user"></i> Basics</h3>
              <span class="section-progress" data-section-progress="basics"></span>
              <i class="fas fa-chevron-down section-toggle"></i>
            </div>
            <div class="form-section-content" id="section-basics">
              <div class="input-group" data-question>
                <label for="user_display_str" class="group-label">First Name or Username</label>
                <div class="input-with-indicator">
                  <input
                    type="text"
                    id="user_display_str"
                    class="input"
                    autocomplete="off"
                    data-lpignore="true"
                    data-1p-ignore
                    value="${isPrivateName(d.profile?.first_name) ? d.profile.first_name : (d.profile?.first_name || '')}"
                    data-field="profile"
                    ${isPrivateName(d.profile?.first_name) ? 'disabled' : ''}
                  />
                  <span class="save-indicator" id="indicator-user_display_str">Saved <i class="fas fa-check"></i></span>
                </div>
                <div class="save-btn-row">
                  <button class="btn btn-primary btn-sm" id="save-first-name">Save</button>
                </div>
                <label class="pnta-checkbox">
                  <input type="checkbox" id="user_display_str_pnta" data-pnta-for="user_display_str"
                    ${isPrivateName(d.profile?.first_name) ? 'checked' : ''} />
                  Prefer not to answer
                </label>
              </div>

              <div class="input-group" data-question>
                <div class="label-with-indicator">
                  <span class="group-label">Birthday <span class="label-hint" id="age-display"></span></span>
                  <span class="save-indicator" id="indicator-birth_date">Saved <i class="fas fa-check"></i></span>
                </div>
                <input
                  type="date"
                  id="birth_date"
                  class="input"
                  value="${d.profile?.birth_date?.split('T')[0] || '2000-01-01'}"
                  data-field="profile"
                />
                <small class="field-note">* Birthday is required to confirm you are 18 or older.<br><br>Your exact birthdate is never shared. If you prefer, select Do not display on profile to hide your age in years.</small>
                ${renderVisiCheckbox('show_age', d.visiJson)}
              </div>

              <div class="input-group">
                <div class="label-with-indicator">
                  <span class="group-label">My Gender <span class="label-hint">I am a...</span></span>
                  <span class="save-indicator" id="indicator-gender_id">Saved <i class="fas fa-check"></i></span>
                </div>
                <div class="radio-group" id="gender_id">
                  ${d.genders.filter(g => g.name !== 'Prefer not to say').map(g => `
                    <label class="radio-item">
                      <input
                        type="radio"
                        name="gender_id"
                        value="${g.id}"
                        ${d.profile?.gender_id === g.id ? 'checked' : ''}
                        data-field="profile"
                      />
                      <span class="radio-circle"></span>
                      ${g.name}
                    </label>
                  `).join('')}
                </div>
                ${renderPntaCheckbox('gender_id', false)}
                ${renderVisiCheckbox('show_gender', d.visiJson)}
              </div>

              <div class="input-group">
                <div class="label-with-indicator">
                  <span class="group-label">My Relationship Status</span>
                  <span class="save-indicator" id="indicator-relationship_status_id">Saved <i class="fas fa-check"></i></span>
                </div>
                <div class="radio-group" id="relationship_status_id">
                  ${d.relationshipStatuses.filter(rs => rs.name !== 'Prefer not to say').map(rs => `
                    <label class="radio-item">
                      <input
                        type="radio"
                        name="relationship_status_id"
                        value="${rs.id}"
                        ${d.profile?.relationship_status_id === rs.id ? 'checked' : ''}
                        data-field="profile"
                      />
                      <span class="radio-circle"></span>
                      ${rs.name}
                    </label>
                  `).join('')}
                </div>
                ${renderPntaCheckbox('relationship_status_id', false)}
                ${renderVisiCheckbox('show_relationship_status', d.visiJson)}
              </div>

            </div>
          </div>

          <!-- WHAT I'M LOOKING FOR SECTION -->
          <div class="form-section">
            <div class="form-section-header" data-section="preferences">
              <h3><i class="fas fa-heart"></i> What I'm Looking For <span class="save-indicator" id="indicator-pref-intent-entries">Saved <i class="fas fa-check"></i></span></h3>
              <span class="section-progress" data-section-progress="preferences"></span>
              <i class="fas fa-chevron-down section-toggle"></i>
            </div>
            <div class="form-section-content" id="section-preferences">
              <div class="input-group">
                <div id="profile-intent-entries-list">
                  ${renderProfileIntentCards(d.profilePrefs.intent_entries?.length ? d.profilePrefs.intent_entries : [{ genders: [], intents: [], age_min: 18, age_max: 99 }])}
                </div>
                <button type="button" id="add-profile-intent-entry-btn" class="btn btn-secondary btn-sm mt-2">
                  <i class="fas fa-plus"></i> Add Another Connection Goal
                </button>
                ${renderVisiCheckbox('show_looking_for', d.visiJson)}
              </div>
            </div>
          </div>

          <!-- TEXT SECTION -->
          <div class="form-section">
            <div class="form-section-header" data-section="text">
              <h3><i class="fas fa-pen"></i> Text</h3>
              <span class="section-progress" data-section-progress="text"></span>
              <i class="fas fa-chevron-down section-toggle"></i>
            </div>
            <div class="form-section-content" id="section-text">
              <div class="input-group" data-question>
                <div class="label-with-counter">
                  <label for="headline" class="group-label">My Headline</label>
                  <span class="char-counter" id="headline-counter">${isPnta(d.about.headline) ? 0 : (d.about.headline || '').length}/${MAX_HEADLINE_LENGTH}</span>
                </div>
                <div class="input-with-indicator">
                  <input
                    type="text"
                    id="headline"
                    class="input"
                    value="${isPnta(d.about.headline) ? '' : (d.about.headline || '')}"
                    placeholder="A short tagline about me"
                    maxlength="${MAX_HEADLINE_LENGTH}"
                    data-field="profile_json"
                    data-json-path="about.headline"
                    ${isPnta(d.about.headline) ? 'disabled' : ''}
                  />
                  <span class="save-indicator" id="indicator-headline">Saved <i class="fas fa-check"></i></span>
                </div>
                <div class="save-btn-row">
                  <button class="btn btn-primary btn-sm" id="save-headline">Save</button>
                </div>
                <label class="pnta-checkbox">
                  <input type="checkbox" id="headline_pnta" data-pnta-for="headline" ${isPnta(d.about.headline) ? 'checked' : ''} />
                  Prefer not to answer
                </label>
              </div>

              <div class="input-group" data-question>
                <div class="label-with-counter">
                  <label for="bio" class="group-label">Short Intro About Me</label>
                  <span class="char-counter" id="bio-counter">${(isPnta(d.profile?.bio) ? 0 : (d.profile?.bio || '').length).toLocaleString()}/${MAX_BIO_LENGTH.toLocaleString()}</span>
                </div>
                <div class="input-with-indicator">
                  <textarea
                    id="bio"
                    class="input"
                    rows="3"
                    placeholder="Tell others about me..."
                    maxlength="${MAX_BIO_LENGTH}"
                    data-field="profile"
                    ${isPnta(d.profile?.bio) ? 'disabled' : ''}
                  >${isPnta(d.profile?.bio) ? '' : (d.profile?.bio || '')}</textarea>
                  <span class="save-indicator" id="indicator-bio">Saved <i class="fas fa-check"></i></span>
                </div>
                <div class="save-btn-row">
                  <button class="btn btn-primary btn-sm" id="save-bio">Save</button>
                </div>
                <label class="pnta-checkbox">
                  <input type="checkbox" id="bio_pnta" data-pnta-for="bio" ${isPnta(d.profile?.bio) ? 'checked' : ''} />
                  Prefer not to answer
                </label>
              </div>
            </div>
          </div>

          <!-- LOCATION SECTION -->
          <div class="form-section">
            <div class="form-section-header" data-section="location">
              <h3><i class="fas fa-map-marker-alt"></i> Where I Live</h3>
              <span class="section-progress" data-section-progress="location"></span>
              <i class="fas fa-chevron-down section-toggle"></i>
            </div>
            <div class="form-section-content" id="section-location">
              <div class="input-group" data-question>
                <label for="city" class="group-label">City</label>
                <div class="input-with-indicator">
                  <input
                    type="text"
                    id="city"
                    class="input"
                    value="${isPnta(d.profile?.city) ? '' : (d.profile?.city || '')}"
                    placeholder="San Francisco"
                    data-field="profile"
                    ${isPnta(d.profile?.city) ? 'disabled' : ''}
                  />
                  <span class="save-indicator" id="indicator-city">Saved <i class="fas fa-check"></i></span>
                </div>
              </div>

              <div class="input-group">
                <label for="state" class="group-label">State/Province</label>
                <div class="input-with-indicator">
                  <input
                    type="text"
                    id="state"
                    class="input"
                    value="${isPnta(d.profile?.state) ? '' : (d.profile?.state || '')}"
                    placeholder="California"
                    data-field="profile"
                    ${isPnta(d.profile?.city) ? 'disabled' : ''}
                  />
                  <span class="save-indicator" id="indicator-state">Saved <i class="fas fa-check"></i></span>
                </div>
              </div>

              <div class="input-group">
                <label for="country" class="group-label">Country</label>
                <div class="input-with-indicator">
                  <input
                    type="text"
                    id="country"
                    class="input"
                    value="${isPnta(d.profile?.country) ? '' : (d.profile?.country || '')}"
                    placeholder="United States"
                    data-field="profile"
                    ${isPnta(d.profile?.city) ? 'disabled' : ''}
                  />
                  <span class="save-indicator" id="indicator-country">Saved <i class="fas fa-check"></i></span>
                </div>
                <div id="location-display" class="age-display"></div>
                <label class="pnta-checkbox">
                  <input type="checkbox" id="location_pnta" data-pnta-for="city,state,country" ${isPnta(d.profile?.city) ? 'checked' : ''} />
                  Prefer not to answer
                </label>
                ${renderVisiCheckbox('show_location', d.visiJson)}
              </div>

              <div class="input-group">
                ${renderRadioGroup('open_to_relocation', FLEXIBILITY_OPTIONS_UI, d.relationship.open_to_relocation, 'profile_json', 'relationship.open_to_relocation', 'Open to Relocation')}
                ${renderVisiCheckbox('show_relocation', d.visiJson)}
              </div>

              <div class="input-group">
                ${renderRadioGroup('open_to_long_distance', FLEXIBILITY_OPTIONS_UI, d.relationship.open_to_long_distance, 'profile_json', 'relationship.open_to_long_distance', 'Open to Long Distance')}
                ${renderVisiCheckbox('show_long_distance', d.visiJson)}
              </div>

              <div class="input-group" data-question>
                <div class="label-with-indicator">
                  <label for="hometown" class="group-label">My Hometown</label>
                  <span class="save-indicator" id="indicator-hometown">Saved <i class="fas fa-check"></i></span>
                </div>
                <div class="input-with-indicator">
                  <input
                    type="text"
                    id="hometown"
                    class="input"
                    value="${isPnta(d.identity.hometown) ? '' : (d.identity.hometown || '')}"
                    maxlength="100"
                    placeholder="Where I grew up"
                    data-field="profile_json"
                    data-json-path="identity.hometown"
                    ${isPnta(d.identity.hometown) ? 'disabled' : ''}
                  />
                </div>
                <label class="pnta-checkbox">
                  <input type="checkbox" id="hometown_pnta" data-pnta-for="hometown" ${isPnta(d.identity.hometown) ? 'checked' : ''} />
                  Prefer not to answer
                </label>
                ${renderVisiCheckbox('show_hometown', d.visiJson)}
              </div>
            </div>
          </div>

          <!-- BODY SECTION -->
          <div class="form-section">
            <div class="form-section-header" data-section="body">
              <h3><i class="fas fa-ruler-vertical"></i> Body</h3>
              <span class="section-progress" data-section-progress="body"></span>
              <i class="fas fa-chevron-down section-toggle"></i>
            </div>
            <div class="form-section-content" id="section-body">
              <div class="input-group" data-question>
                <div class="label-with-indicator">
                  <span class="group-label">My Height</span>
                  <span class="save-indicator" id="indicator-height">Saved <i class="fas fa-check"></i></span>
                </div>
                <div class="height-input-container" id="height-inputs-container">
                  <!-- Metric: cm input -->
                  <div class="height-metric-col" id="height-metric-inputs" style="${d.units === 'metric' ? '' : 'display: none;'}">
                    <input
                      type="number"
                      id="height_cm"
                      class="input"
                      value="${d.profile?.height_cm || ''}"
                      min="${MIN_HEIGHT_CM}"
                      max="${MAX_HEIGHT_CM}"
                      placeholder=""
                      data-field="profile"
                      ${d.body.height_pnta ? 'disabled' : ''}
                    />
                    <span class="height-unit-label">cm</span>
                  </div>
                  <!-- Imperial: ft/in inputs -->
                  <div class="height-imperial-col" id="height-imperial-inputs" style="${d.units === 'imperial' ? '' : 'display: none;'}">
                    <input type="number" id="height_feet" class="input height-feet-input" min="1" max="9" placeholder="" ${d.body.height_pnta ? 'disabled' : ''} />
                    <span class="height-unit-label">ft</span>
                    <input type="number" id="height_inches" class="input height-inches-input" min="0" max="11" step="1" placeholder="" ${d.body.height_pnta ? 'disabled' : ''} />
                    <span class="height-unit-label">in</span>
                  </div>
                </div>
                <label class="pnta-checkbox">
                  <input type="checkbox" id="height_pnta" ${d.body.height_pnta ? 'checked' : ''} />
                  Prefer not to answer
                </label>
                ${renderVisiCheckbox('show_height', d.visiJson)}
              </div>

              <div class="input-group" data-question>
                <div class="label-with-indicator">
                  <span class="group-label">My Weight</span>
                  <span class="save-indicator" id="indicator-weight">Saved <i class="fas fa-check"></i></span>
                </div>
                <div class="height-input-container" id="weight-inputs-container">
                  <!-- Metric: kg input -->
                  <div class="height-metric-col" id="weight-metric-inputs" style="${d.units === 'metric' ? '' : 'display: none;'}">
                    <input
                      type="number"
                      id="weight_kg"
                      class="input"
                      value="${d.body.weight_lb ? Math.round(d.body.weight_lb * 0.453592) : ''}"
                      min="20"
                      max="500"
                      placeholder=""
                      ${d.body.weight_pnta ? 'disabled' : ''}
                    />
                    <span class="height-unit-label">kg</span>
                  </div>
                  <!-- Imperial: lb input -->
                  <div class="height-metric-col" id="weight-imperial-inputs" style="${d.units === 'imperial' ? '' : 'display: none;'}">
                    <input
                      type="number"
                      id="weight_lb"
                      class="input"
                      value="${d.body.weight_lb || ''}"
                      min="50"
                      max="1000"
                      placeholder=""
                      data-field="profile_json"
                      data-json-path="body.weight_lb"
                      ${d.body.weight_pnta ? 'disabled' : ''}
                    />
                    <span class="height-unit-label">lb</span>
                  </div>
                </div>
                <label class="pnta-checkbox">
                  <input type="checkbox" id="weight_pnta" ${d.body.weight_pnta ? 'checked' : ''} />
                  Prefer not to answer
                </label>
                ${renderVisiCheckbox('show_weight', d.visiJson)}
              </div>

              <div class="input-group">
                ${renderRadioGroup('body_type', BODY_TYPE_OPTIONS, d.body.body_type, 'profile_json', 'body.body_type', 'My Body Type')}
                ${renderVisiCheckbox('show_body_type', d.visiJson)}
              </div>
            </div>
          </div>

          <!-- FAMILY SECTION -->
          <div class="form-section">
            <div class="form-section-header" data-section="family">
              <h3><i class="fas fa-child"></i> Family</h3>
              <span class="section-progress" data-section-progress="family"></span>
              <i class="fas fa-chevron-down section-toggle"></i>
            </div>
            <div class="form-section-content" id="section-family">
              <div class="input-group">
                ${renderRadioGroup('has_kids', HAS_KIDS_OPTIONS, d.kids.has_kids, 'profile_json', 'kids.has_kids', 'Do you have kids?')}
                ${renderVisiCheckbox('show_kids', d.visiJson)}
              </div>

              <!-- Children Entry Section (visible when has_kids = 'yes') -->
              <div class="input-group conditional-field children-field" id="children-section" style="${d.kids.has_kids === 'yes' ? '' : 'display: none;'}">
                <div class="label-with-indicator">
                  <span class="group-label">My kids</span>
                  <span class="save-indicator" id="indicator-children">Saved <i class="fas fa-check"></i></span>
                </div>
                <div id="children-list">
                  ${renderChildrenList(d.kids.children || [])}
                </div>
                <button type="button" class="btn btn-outline btn-sm" id="add-child-btn">
                  <i class="fas fa-plus"></i> Add Child
                </button>
              </div>

              <div class="input-group">
                ${renderRadioGroup('wants_more_kids', WANTS_MORE_KIDS_OPTIONS, d.kids.wants_more_kids, 'profile_json', 'kids.wants_more_kids', wantsKidsLabel(d.kids.has_kids === 'yes', 'profile'))}
                ${renderVisiCheckbox('show_wants_more_kids', d.visiJson)}
              </div>
            </div>
          </div>

          <!-- PETS SECTION -->
          <div class="form-section">
            <div class="form-section-header" data-section="pets">
              <h3><i class="fas fa-paw"></i> Pets</h3>
              <span class="section-progress" data-section-progress="pets"></span>
              <i class="fas fa-chevron-down section-toggle"></i>
            </div>
            <div class="form-section-content" id="section-pets">
              <div class="input-group">
                <span class="group-label">Pets I Have</span>
                <div class="checkbox-group" id="pets_has">
                  ${renderCheckboxGroup('pets_has', PET_TYPE_OPTIONS, Array.isArray(d.pets.has) ? d.pets.has : (d.pets.has === 'none' ? ['none'] : []), 'profile_json')}
                </div>
                <span class="save-indicator" id="indicator-pets_has">Saved <i class="fas fa-check"></i></span>
                ${renderVisiCheckbox('show_pets', d.visiJson)}
              </div>

              <div class="input-group">
                <span class="group-label">Pets I Want</span>
                <div class="checkbox-group" id="pets_wants">
                  ${renderCheckboxGroup('pets_wants', PET_TYPE_OPTIONS, Array.isArray(d.pets.wants) ? d.pets.wants : (d.pets.wants === 'none' ? ['none'] : []), 'profile_json')}
                </div>
                <span class="save-indicator" id="indicator-pets_wants">Saved <i class="fas fa-check"></i></span>
                ${renderVisiCheckbox('show_pets_wants', d.visiJson)}
              </div>
            </div>
          </div>

          <!-- IDENTITY SECTION -->
          <div class="form-section">
            <div class="form-section-header" data-section="identity">
              <h3><i class="fas fa-id-card"></i> Identity</h3>
              <span class="section-progress" data-section-progress="identity"></span>
              <i class="fas fa-chevron-down section-toggle"></i>
            </div>
            <div class="form-section-content" id="section-identity">
              <div class="input-group">
                ${renderMultiSelectWithCustomField('pronouns', PRONOUNS_OPTIONS, d.identity.pronouns, d.identity.pronouns_custom, 'identity.pronouns', 'identity.pronouns_custom', 'My Pronouns')}
                ${renderVisiCheckbox('show_pronouns', d.visiJson)}
              </div>

              <div class="input-group">
                ${renderMultiSelectWithOther('sexual_orientation', SEXUAL_ORIENTATION_OPTIONS, d.identity.sexual_orientation, 'identity.sexual_orientation', 'My Sexual Orientation')}
                ${renderVisiCheckbox('show_orientation', d.visiJson)}
              </div>

              <div class="input-group">
                <div class="label-with-indicator">
                  <span class="group-label">My Ethnicity</span>
                  <span class="save-indicator" id="indicator-ethnicity">Saved <i class="fas fa-check"></i></span>
                </div>
                <div class="checkbox-group" id="ethnicity">
                  ${renderCheckboxGroup('ethnicity', ETHNICITY_OPTIONS, d.identity.ethnicity || [], 'profile_json')}
                </div>
                ${renderVisiCheckbox('show_ethnicity', d.visiJson)}
              </div>

              <div class="input-group">
                ${renderRadioGroupWithOther('religion', RELIGION_OPTIONS, d.identity.religion, 'identity.religion', 'My Religion')}
                ${renderVisiCheckbox('show_religion', d.visiJson)}
              </div>

              <div class="input-group">
                ${renderRadioGroup('political_view', POLITICAL_VIEW_OPTIONS, d.identity.political_view, 'profile_json', 'identity.political_view', 'My Political Views')}
                ${renderVisiCheckbox('show_political_view', d.visiJson)}
              </div>

              <div class="input-group">
                ${renderMultiSelectWithOther('languages', LANGUAGES_OPTIONS, d.identity.languages, 'identity.languages', 'Languages I Speak')}
                ${renderVisiCheckbox('show_languages', d.visiJson)}
              </div>

              <div class="input-group">
                ${renderRadioGroup('zodiac_sign', ZODIAC_OPTIONS, d.identity.zodiac_sign, 'profile_json', 'identity.zodiac_sign', 'My Star Sign')}
                ${renderVisiCheckbox('show_zodiac', d.visiJson)}
                <small class="zodiac-hint" id="zodiac-hint"></small>
              </div>

            </div>
          </div>

          <!-- WORK & EDUCATION SECTION -->
          <div class="form-section">
            <div class="form-section-header" data-section="work">
              <h3><i class="fas fa-briefcase"></i> Work & Education</h3>
              <span class="section-progress" data-section-progress="work"></span>
              <i class="fas fa-chevron-down section-toggle"></i>
            </div>
            <div class="form-section-content" id="section-work">
              <div class="input-group">
                ${renderRadioGroup('education_level', EDUCATION_LEVEL_OPTIONS, d.background.education_level, 'profile_json', 'background.education_level', 'My Education')}
                ${renderVisiCheckbox('show_education', d.visiJson)}
              </div>

              <div class="input-group">
                ${renderRadioGroup('employment_status', EMPLOYMENT_STATUS_OPTIONS, d.background.employment_status, 'profile_json', 'background.employment_status', 'My Employment')}
                ${renderVisiCheckbox('show_employment', d.visiJson)}
              </div>

              <!-- Student fields (shown when employment_status === 'student') -->
              <div class="input-group conditional-field student-field" data-question data-show-when="student" style="${d.background.employment_status === 'student' ? '' : 'display: none;'}">
                <div class="label-with-indicator">
                  <label for="school" class="group-label">My School</label>
                  <span class="save-indicator" id="indicator-school">Saved <i class="fas fa-check"></i></span>
                </div>
                <div class="input-with-indicator">
                  <input
                    type="text"
                    id="school"
                    class="input"
                    value="${isPnta(d.profile?.school) ? '' : (d.profile?.school || '')}"
                    placeholder="University name"
                    data-field="profile"
                    ${isPnta(d.profile?.school) ? 'disabled' : ''}
                  />
                </div>
                <label class="pnta-checkbox">
                  <input type="checkbox" id="school_pnta" data-pnta-for="school" ${isPnta(d.profile?.school) ? 'checked' : ''} />
                  Prefer not to answer
                </label>
                ${renderVisiCheckbox('show_school', d.visiJson)}
              </div>

              <div class="input-group conditional-field student-field" data-show-when="student" style="${d.background.employment_status === 'student' ? '' : 'display: none;'}">
                ${renderRadioGroupWithOther('major', MAJOR_OPTIONS, d.background.major, 'background.major', 'My Major')}
              </div>

              <!-- Work fields (shown when employed: full_time, part_time, self_employed) -->
              <div class="input-group conditional-field work-field" data-question data-show-when="employed" style="${['full_time', 'part_time', 'self_employed'].includes(d.background.employment_status || '') ? '' : 'display: none;'}">
                <div class="label-with-indicator">
                  <label for="company" class="group-label">My Company</label>
                  <span class="save-indicator" id="indicator-company">Saved <i class="fas fa-check"></i></span>
                </div>
                <div class="input-with-indicator">
                  <input
                    type="text"
                    id="company"
                    class="input"
                    value="${isPnta(d.profile?.company) ? '' : (d.profile?.company || '')}"
                    placeholder="Where I work"
                    data-field="profile"
                    ${isPnta(d.profile?.company) ? 'disabled' : ''}
                  />
                </div>
                <label class="pnta-checkbox">
                  <input type="checkbox" id="company_pnta" data-pnta-for="company" ${isPnta(d.profile?.company) ? 'checked' : ''} />
                  Prefer not to answer
                </label>
                ${renderVisiCheckbox('show_company', d.visiJson)}
              </div>

              <div class="input-group conditional-field work-field" data-question data-show-when="employed" style="${['full_time', 'part_time', 'self_employed'].includes(d.background.employment_status || '') ? '' : 'display: none;'}">
                <div class="label-with-indicator">
                  <label for="job_title" class="group-label">My Job Title</label>
                  <span class="save-indicator" id="indicator-job_title">Saved <i class="fas fa-check"></i></span>
                </div>
                <div class="input-with-indicator">
                  <input
                    type="text"
                    id="job_title"
                    class="input"
                    value="${isPnta(d.profile?.job_title) ? '' : (d.profile?.job_title || '')}"
                    placeholder="What I do"
                    data-field="profile"
                    ${isPnta(d.profile?.job_title) ? 'disabled' : ''}
                  />
                </div>
                <label class="pnta-checkbox">
                  <input type="checkbox" id="job_title_pnta" data-pnta-for="job_title" ${isPnta(d.profile?.job_title) ? 'checked' : ''} />
                  Prefer not to answer
                </label>
                ${renderVisiCheckbox('show_job_title', d.visiJson)}
              </div>

              <div class="input-group conditional-field work-field" data-show-when="employed" style="${['full_time', 'part_time', 'self_employed'].includes(d.background.employment_status || '') ? '' : 'display: none;'}">
                ${renderRadioGroupWithOther('career_field', CAREER_FIELD_OPTIONS, d.background.career_field, 'background.career_field', 'My Career Field')}
                ${renderVisiCheckbox('show_career_field', d.visiJson)}
              </div>

              <div class="input-group conditional-field work-field" data-show-when="employed" style="${['full_time', 'part_time', 'self_employed'].includes(d.background.employment_status || '') ? '' : 'display: none;'}">
                ${renderRadioGroup('income_range', INCOME_RANGE_OPTIONS, d.background.income_range, 'profile_json', 'background.income_range', 'My Income')}
                ${renderVisiCheckbox('show_income', d.visiJson)}
              </div>
            </div>
          </div>

          <!-- LIFESTYLE SECTION -->
          <div class="form-section">
            <div class="form-section-header" data-section="lifestyle">
              <h3><i class="fas fa-wine-glass"></i> My Lifestyle</h3>
              <span class="section-progress" data-section-progress="lifestyle"></span>
              <i class="fas fa-chevron-down section-toggle"></i>
            </div>
            <div class="form-section-content" id="section-lifestyle">
              <div class="input-group">
                ${renderRadioGroup('smoking', FREQUENCY_OPTIONS, d.lifestyle.smoking, 'profile_json', 'lifestyle.smoking', 'Smoking')}
                ${renderVisiCheckbox('show_smoking', d.visiJson)}
              </div>

              <div class="input-group">
                ${renderRadioGroup('drinking', FREQUENCY_OPTIONS, d.lifestyle.drinking, 'profile_json', 'lifestyle.drinking', 'Drinking')}
                ${renderVisiCheckbox('show_drinking', d.visiJson)}
              </div>

              <div class="input-group">
                ${renderRadioGroup('cannabis', FREQUENCY_OPTIONS, d.lifestyle.cannabis, 'profile_json', 'lifestyle.cannabis', 'Cannabis')}
                ${renderVisiCheckbox('show_cannabis', d.visiJson)}
              </div>

              <div class="input-group">
                ${renderRadioGroup('drugs', FREQUENCY_OPTIONS, d.lifestyle.drugs, 'profile_json', 'lifestyle.drugs', 'Drugs')}
                ${renderVisiCheckbox('show_drugs', d.visiJson)}
              </div>

              <div class="input-group">
                ${renderRadioGroup('exercise', EXERCISE_OPTIONS, d.lifestyle.exercise, 'profile_json', 'lifestyle.exercise', 'Exercise')}
                ${renderVisiCheckbox('show_exercise', d.visiJson)}
              </div>
            </div>
          </div>

          <!-- RELATIONSHIP DYNAMICS SECTION -->
          <div class="form-section">
            <div class="form-section-header" data-section="disclosures">
              <h3><i class="fas fa-rainbow"></i> Relationship Dynamics</h3>
              <span class="section-progress" data-section-progress="disclosures"></span>
              <i class="fas fa-chevron-down section-toggle"></i>
            </div>
            <div class="form-section-content" id="section-disclosures">
              <div class="input-group">
                ${renderRadioGroup('relationship_structure', RELATIONSHIP_STRUCTURE_OPTIONS, d.relationship.structure, 'profile_json', 'relationship.structure', "Relationship structure I'm open to")}
                ${renderVisiCheckbox('show_rel_structure', d.visiJson)}
              </div>

              <div class="input-group">
                <div class="label-with-indicator">
                  <span class="group-label">Who I'm Open To</span>
                  <span class="save-indicator" id="indicator-disclosures">Saved <i class="fas fa-check"></i></span>
                </div>
                <div class="checkbox-group single-column" id="disclosures">
                  <label class="checkbox-item">
                    <input
                      type="checkbox"
                      id="inclusive_of_trans_nb"
                      name="disclosures"
                      value="inclusive_of_trans_nb"
                      ${d.disclosures.inclusive_of_trans_nb ? 'checked' : ''}
                      data-field="profile_json"
                      data-json-path="disclosures.inclusive_of_trans_nb"
                    />
                    Open to trans and non-binary people
                  </label>
                  <label class="checkbox-item">
                    <input
                      type="checkbox"
                      id="open_to_enm_poly"
                      name="disclosures"
                      value="open_to_enm_poly"
                      ${d.disclosures.open_to_enm_poly ? 'checked' : ''}
                      data-field="profile_json"
                      data-json-path="disclosures.open_to_enm_poly"
                    />
                    Open to ENM/poly dynamics
                  </label>
                  <label class="checkbox-item">
                    <input
                      type="checkbox"
                      id="no_other_groups"
                      name="disclosures"
                      value="no_other_groups"
                      ${d.disclosures.no_other_groups ? 'checked' : ''}
                      data-field="profile_json"
                      data-json-path="disclosures.no_other_groups"
                      data-exclusive="true"
                    />
                    No other groups
                  </label>
                  <label class="pnta-checkbox">
                    <input
                      type="checkbox"
                      id="disclosures_pnta"
                      data-pnta-for-group="disclosures"
                      ${d.disclosures.pnta ? 'checked' : ''}
                    />
                    Prefer not to answer
                  </label>
                </div>
                ${renderVisiCheckbox('show_disclosures', d.visiJson)}
              </div>
            </div>
          </div>

          <!-- INTIMACY SECTION -->
          <div class="form-section">
            <div class="form-section-header" data-section="sexual">
              <h3><i class="fas fa-check"></i> Intimacy</h3>
              <span class="section-progress" data-section-progress="sexual"></span>
              <i class="fas fa-chevron-down section-toggle"></i>
            </div>
            <div class="form-section-content" id="section-sexual">
              <div class="input-group">
                <div class="label-with-indicator">
                  <span class="group-label">My Role</span>
                  <span class="save-indicator" id="indicator-sexual_role">Saved <i class="fas fa-check"></i></span>
                </div>
                <div class="checkbox-group" id="sexual_role">
                  ${renderCheckboxGroup('sexual_role', SEXUAL_ROLE_OPTIONS, Array.isArray(d.sexual.role) ? d.sexual.role : (d.sexual.role ? [d.sexual.role] : []), 'profile_json')}
                </div>
                ${renderVisiCheckbox('show_sexual_role', d.visiJson)}
              </div>

              <div class="input-group">
                <div class="label-with-indicator">
                  <span class="group-label">My Dynamics</span>
                  <span class="save-indicator" id="indicator-sexual_dynamics">Saved <i class="fas fa-check"></i></span>
                </div>
                <div class="checkbox-group" id="sexual_dynamics">
                  ${renderCheckboxGroup('sexual_dynamics', SEXUAL_DYNAMICS_OPTIONS, Array.isArray(d.sexual.dynamics) ? d.sexual.dynamics : (d.sexual.dynamics ? [d.sexual.dynamics] : []), 'profile_json')}
                </div>
                ${renderVisiCheckbox('show_sexual_dynamics', d.visiJson)}
              </div>
            </div>
          </div>

        </div>
      </div>
  `;
}
